import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Bookmark, User, Mail, Calendar, Euro, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { APPLICATION_STATUS_LABELS, EXPERTISE, PROVINCES } from "../lib/constants";

function CVUploader({ profile, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Alleen PDF bestanden toegestaan.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("CV geupload.");
      onUploaded(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      {profile.resume_path && (
        <a
          href={`${API}/resume/download/${profile.user_id}`}
          target="_blank"
          rel="noreferrer"
          data-testid="profile-cv-download"
          className="inline-flex items-center gap-2 text-sm text-[#01696f] hover:underline"
        >
          <Download size={14} /> Huidig CV: {profile.resume_filename || "cv.pdf"}
        </a>
      )}
      <label className="flex items-center gap-3 p-3 border border-dashed border-[#01696f] bg-[#e6f0f1]/40 cursor-pointer hover:bg-[#e6f0f1] text-sm">
        <Upload size={16} className="text-[#01696f]" />
        <span>{busy ? "Bezig met uploaden..." : profile.resume_path ? "Nieuw CV uploaden (vervangt huidige)" : "Klik om een PDF te uploaden"}</span>
        <input data-testid="profile-cv-upload" type="file" accept="application/pdf" className="hidden" onChange={upload} disabled={busy} />
      </label>
    </div>
  );
}

export default function KandidaatDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [apps, setApps] = useState([]);
  const [saved, setSaved] = useState([]);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    const [p, a, s] = await Promise.all([
      api.get("/profile/candidate"),
      api.get("/applications/mine"),
      api.get("/saved-vacancies"),
    ]);
    // Normalize: ensure expertise_categories array exists (fallback to legacy single field)
    const profileData = p.data;
    if (!Array.isArray(profileData.expertise_categories) || profileData.expertise_categories.length === 0) {
      profileData.expertise_categories = profileData.expertise_category ? [profileData.expertise_category] : [];
    }
    setProfile(profileData); setApps(a.data); setSaved(s.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.expertise_categories || profile.expertise_categories.length === 0) {
      toast.error("Kies minstens één expertisegebied.");
      return;
    }
    try {
      const { data } = await api.put("/profile/candidate", profile);
      const normalized = data;
      if (!Array.isArray(normalized.expertise_categories) || normalized.expertise_categories.length === 0) {
        normalized.expertise_categories = normalized.expertise_category ? [normalized.expertise_category] : [];
      }
      setProfile(normalized);
      setEditing(false);
      toast.success("Profiel bijgewerkt.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleProfileExpertise = (x) => {
    setProfile((p) => {
      const cats = p.expertise_categories || [];
      return {
        ...p,
        expertise_categories: cats.includes(x) ? cats.filter((y) => y !== x) : [...cats, x],
      };
    });
  };
  const toggleProfileProvince = (x) => {
    setProfile((p) => {
      const provs = p.province_preference || [];
      return {
        ...p,
        province_preference: provs.includes(x) ? provs.filter((y) => y !== x) : [...provs, x],
      };
    });
  };

  if (!profile) return <div className="ok-container py-20 text-sm">Laden…</div>;

  return (
    <section className="py-12">
      <div className="ok-container">
        <div className="ok-eyebrow mb-2">Kandidaat-dashboard</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight" data-testid="cand-dash-title">
          Hallo {profile.first_name}.
        </h1>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-4 space-y-6">
            <div className="border border-[#e2e8f0] p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#01696f] text-white grid place-items-center font-display font-bold">
                  {profile.first_name?.[0]}{profile.last_name?.[0]}
                </div>
                <div>
                  <div className="font-semibold">{profile.first_name} {profile.last_name}</div>
                  <div className="text-xs text-[#4a5d5e]">{(profile.expertise_categories || []).join(", ") || profile.expertise_category}</div>
                </div>
              </div>
              <div className="mt-5 space-y-2 text-sm text-[#4a5d5e]">
                <div className="flex items-center gap-2"><Mail size={14} />{profile.email}</div>
                <div className="flex items-center gap-2"><Calendar size={14} />Beschikbaar: {profile.available_from || "n.v.t."}</div>
                <div className="flex items-center gap-2"><Euro size={14} />Tarief: € {profile.hourly_rate || "n.v.t."}/u</div>
                <div className="flex items-center gap-2"><Briefcase size={14} />{profile.years_experience} jaar ervaring</div>
              </div>
              <button data-testid="edit-profile-btn" onClick={() => setEditing(!editing)}
                className="mt-5 w-full border border-[#0a191a] py-2 rounded-sm text-sm hover:bg-[#0a191a] hover:text-white">
                {editing ? "Annuleren" : "Profiel bewerken"}
              </button>
            </div>

            <div className="border border-[#e2e8f0] p-6">
              <label className="flex items-start gap-3 text-sm">
                <input data-testid="newsletter-toggle" type="checkbox" className="mt-1"
                  checked={profile.newsletter_subscribed}
                  onChange={async (e) => {
                    const v = e.target.checked;
                    setProfile({ ...profile, newsletter_subscribed: v });
                    await api.put("/profile/candidate", { newsletter_subscribed: v });
                    toast.success(v ? "Aangemeld voor nieuwsbrief" : "Uitgeschreven");
                  }} />
                <span>Stuur mij nieuwsbrieven met passende opdrachten.</span>
              </label>
            </div>
          </aside>

          <div className="lg:col-span-8 space-y-8">
            {editing && (
              <form onSubmit={saveProfile} className="border border-[#e2e8f0] p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <KF label="Voornaam"><input className="ok-input" value={profile.first_name || ""} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} /></KF>
                  <KF label="Achternaam"><input className="ok-input" value={profile.last_name || ""} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} /></KF>
                  <KF label="Telefoonnummer"><input className="ok-input" value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></KF>
                  <KF label="LinkedIn URL"><input className="ok-input" placeholder="https://linkedin.com/in/..." value={profile.linkedin_url || ""} onChange={(e) => setProfile({ ...profile, linkedin_url: e.target.value })} /></KF>
                  <KF label="Jaren werkervaring"><input type="number" className="ok-input" value={profile.years_experience || 0} onChange={(e) => setProfile({ ...profile, years_experience: Number(e.target.value) })} /></KF>
                  <KF label="Beschikbaar vanaf"><input type="date" className="ok-input" value={profile.available_from || ""} onChange={(e) => setProfile({ ...profile, available_from: e.target.value })} /></KF>
                  <KF label="Gewenst dienstverband">
                    <select data-testid="profile-contract" className="ok-input" value={profile.contract_type || "Beide"} onChange={(e) => setProfile({ ...profile, contract_type: e.target.value })}>
                      <option value="Detacheren/dienstverband">Detacheren/dienstverband</option>
                      <option value="ZZP">ZZP</option>
                      <option value="Beide">Beide</option>
                    </select>
                  </KF>
                </div>

                <div className="border border-[#e2e8f0] p-4">
                  <div className="ok-eyebrow mb-3">Adresgegevens (NAW)</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <KF label="Straat en huisnummer"><input data-testid="profile-address" className="ok-input" value={profile.address || ""} onChange={(e) => setProfile({ ...profile, address: e.target.value })} /></KF>
                    <KF label="Postcode"><input data-testid="profile-postcode" className="ok-input" value={profile.postcode || ""} onChange={(e) => setProfile({ ...profile, postcode: e.target.value })} /></KF>
                    <KF label="Woonplaats"><input data-testid="profile-city" className="ok-input" value={profile.city || ""} onChange={(e) => setProfile({ ...profile, city: e.target.value })} /></KF>
                  </div>
                </div>

                {(profile.contract_type === "ZZP" || profile.contract_type === "Beide") && (
                  <div className="border border-[#e2e8f0] p-4 bg-[#f7f6f2]">
                    <div className="ok-eyebrow mb-3">ZZP tariefrange (€/uur)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <KF label="Minimum tarief"><input data-testid="profile-rate-min" type="number" step="0.5" className="ok-input" value={profile.hourly_rate_min || ""} onChange={(e) => setProfile({ ...profile, hourly_rate_min: e.target.value ? Number(e.target.value) : null })} /></KF>
                      <KF label="Maximum tarief"><input data-testid="profile-rate-max" type="number" step="0.5" className="ok-input" value={profile.hourly_rate_max || ""} onChange={(e) => setProfile({ ...profile, hourly_rate_max: e.target.value ? Number(e.target.value) : null })} /></KF>
                    </div>
                  </div>
                )}
                {(profile.contract_type === "Detacheren/dienstverband" || profile.contract_type === "Beide") && (
                  <div className="border border-[#e2e8f0] p-4 bg-[#f7f6f2]">
                    <div className="ok-eyebrow mb-3">Detacheren/dienstverband salarisrange (€/maand bruto)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <KF label="Minimum salaris"><input data-testid="profile-salary-min" type="number" className="ok-input" value={profile.salary_min || ""} onChange={(e) => setProfile({ ...profile, salary_min: e.target.value ? Number(e.target.value) : null })} /></KF>
                      <KF label="Maximum salaris"><input data-testid="profile-salary-max" type="number" className="ok-input" value={profile.salary_max || ""} onChange={(e) => setProfile({ ...profile, salary_max: e.target.value ? Number(e.target.value) : null })} /></KF>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-[#4a5d5e] mb-2">
                    Expertisegebieden (meerdere mogelijk)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {EXPERTISE.map((x) => {
                      const active = (profile.expertise_categories || []).includes(x);
                      return (
                        <button
                          key={x}
                          type="button"
                          data-testid={`profile-expertise-${x}`}
                          onClick={() => toggleProfileExpertise(x)}
                          className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${active ? "bg-[#01696f] text-white border-[#01696f]" : "bg-white border-[#e2e8f0] hover:border-[#01696f]"}`}
                        >
                          {x}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider font-semibold text-[#4a5d5e] mb-2">
                    Provincievoorkeur (meerdere mogelijk)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PROVINCES.map((p) => {
                      const active = (profile.province_preference || []).includes(p);
                      return (
                        <button
                          key={p}
                          type="button"
                          data-testid={`profile-province-${p}`}
                          onClick={() => toggleProfileProvince(p)}
                          className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${active ? "bg-[#01696f] text-white border-[#01696f]" : "bg-white border-[#e2e8f0] hover:border-[#01696f]"}`}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <KF label="Korte motivatie">
                  <textarea className="ok-input w-full" rows={3} value={profile.motivation || ""} onChange={(e) => setProfile({ ...profile, motivation: e.target.value })} />
                </KF>
                <button data-testid="save-profile-btn" className="bg-[#01696f] text-white px-6 py-2.5 rounded-sm hover:bg-[#015257]">Profiel opslaan</button>
              </form>
            )}

            {/* Mijn CV sectie */}
            <div className="border border-[#e2e8f0] p-6">
              <h2 className="font-display text-xl font-semibold mb-4">Mijn CV</h2>
              {profile.resume_path ? (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-[#f7f6f2] border border-[#e2e8f0]">
                  <div className="flex items-center gap-3">
                    <Download size={18} className="text-[#01696f]" />
                    <div>
                      <div className="font-semibold text-sm">{profile.resume_filename || "cv.pdf"}</div>
                      <div className="text-xs text-[#4a5d5e]">PDF · klik om te bekijken of vervang het bestand hieronder</div>
                    </div>
                  </div>
                  <a
                    href={`${API}/resume/download/${profile.user_id}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="cv-section-view"
                    className="inline-flex items-center justify-center gap-2 border border-[#0a191a] px-4 py-2 rounded-sm text-sm hover:bg-[#0a191a] hover:text-white whitespace-nowrap"
                  >
                    Bekijk CV
                  </a>
                </div>
              ) : (
                <p className="text-sm text-[#7b8e8f] mb-3">Je hebt nog geen CV geupload.</p>
              )}
              <div className="mt-4">
                <CVUploader profile={profile} onUploaded={(f) => setProfile({ ...profile, resume_path: f.path, resume_filename: f.filename })} />
              </div>
            </div>

            <div>
              <h2 className="font-display text-xl font-semibold mb-4">Mijn sollicitaties</h2>
              {apps.length === 0 ? (
                <p className="text-sm text-[#7b8e8f] border border-dashed border-[#e2e8f0] p-6">Nog geen sollicitaties.</p>
              ) : (
                <div className="border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
                  {apps.map((a) => (
                    <div key={a.id} className="p-4 flex items-center justify-between hover:bg-[#f7f6f2]" data-testid={`application-${a.id}`}>
                      <div>
                        <Link to={`/vacature/${a.vacancy_id}`} className="font-semibold hover:text-[#01696f]">
                          {a.vacancy?.title || "Vacature"}
                        </Link>
                        <div className="text-xs text-[#4a5d5e] mt-1">{a.vacancy?.location}</div>
                      </div>
                      <span className="ok-chip">{APPLICATION_STATUS_LABELS[a.status] || a.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2"><Bookmark size={18} /> Opgeslagen vacatures</h2>
              {saved.length === 0 ? (
                <p className="text-sm text-[#7b8e8f] border border-dashed border-[#e2e8f0] p-6">Nog niets opgeslagen.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {saved.map((v) => (
                    <Link key={v.id} to={`/vacature/${v.id}`} className="ok-card p-5" data-testid={`saved-${v.id}`}>
                      <div className="font-semibold">{v.title}</div>
                      <div className="text-xs text-[#4a5d5e] mt-2">{v.location} · {v.hours_per_week}u/wk</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`.ok-input { width: 100%; padding: 0.5rem 0.625rem; border: 1px solid #e2e8f0; background: #fff; font-size: 0.875rem; } .ok-input:focus { outline: 2px solid #01696f; outline-offset: 1px; }`}</style>
    </section>
  );
}

function KF({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[#4a5d5e] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
